/**
 * `/recruiting/offers` — the Offers screen, WP-W5.
 * `web/shapes/offers.md` is the agreed shape, written before this file.
 *
 * The fifth and last recruiting surface, on the merged W1–W4 foundation. It consumes the
 * offers API (create / list-per-application / approve / send / respond) and serves one
 * persona: the recruiter / hiring coordinator holding `recruiting.manage_offers` (the
 * menu item's requirement, and every write's). The route guard in `App.tsx` renders
 * permission-denied at this URL for a session without it; the same denial answers again
 * through the API, the other door PRODUCT.md §3 requires the state at.
 *
 * ── Offers list per application, so an application is the first question ──────
 *
 * There is no company-wide offers list — `GET /recruiting/applications/{id}/offers` is
 * the only read — so the screen asks for an application first, then lists its offers
 * (newest first) and offers a create-draft form against it. The application is chosen
 * from the applications list, labelled by candidate name resolved through the candidates
 * list; when either list is out of reach the field falls back to a plain id input, the
 * same honest fallback the pipeline and interviews screens use.
 *
 * ── The lifecycle is three verbs, and the illegal ones have no button ────────
 *
 * An offer moves `draft/pending_approval → approved` (approve, approver stamped
 * server-side), `approved → sent` (send), `sent → {accepted, declined}` (respond). The
 * per-offer `OfferStatusControls` renders exactly the actions the offer's status allows
 * and nothing else; a terminal offer renders one sentence. The server's `409
 * WORKFORCE_INVALID_STATE_TRANSITION` is authoritative and its `allowed_transitions[]`
 * is printed verbatim beside the refusal.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import type {
  OfferResponseOutcome,
  RecruitingApplicationListResponse,
  RecruitingCandidateListResponse,
  RecruitingOffer,
  RecruitingOfferCreateRequest,
  RecruitingOfferListResponse,
  RecruitingOfferRespondRequest,
} from '../../api/recruiting';
import {
  recruitingApplicationOffersPath,
  recruitingApplicationsPath,
  recruitingCandidatesPath,
  recruitingOfferApprovePath,
  recruitingOfferRespondPath,
  recruitingOfferSendPath,
} from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { OFFER_STATUS_BADGE, OFFER_STATUS_LABEL, type OfferAction } from '../../recruiting/offer';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { CreateOfferForm } from './CreateOfferForm';
import { OfferStatusControls } from './OfferStatusControls';
import type { PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export function OffersScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [application, setApplication] = useState('');
  const [offersToken, setOffersToken] = useState(0);
  // The offer the last action was aimed at — so a refusal or a spinner is attributed to
  // that offer's controls and not to every row at once.
  const [actionOfferId, setActionOfferId] = useState<string | null>(null);

  const trimmedApplication = application.trim();
  const offersPath =
    trimmedApplication === '' ? null : recruitingApplicationOffersPath(trimmedApplication);
  const offers = useApiQuery<RecruitingOfferListResponse>(offersPath, offersToken);

  // Enrichment reads for name resolution and the application picker. Independent regions
  // on `recruiting.view_candidates`; a manage_offers-only session sees them refused and
  // falls back to an id input.
  const applications = useApiQuery<RecruitingApplicationListResponse>(
    recruitingApplicationsPath({ limit: PAGE_LIMIT }),
  );
  const candidates = useApiQuery<RecruitingCandidateListResponse>(
    recruitingCandidatesPath({ limit: PAGE_LIMIT }),
  );

  const create = useApiMutation<RecruitingOfferCreateRequest, RecruitingOffer>(
    recruitingApplicationOffersPath('unset'),
    'POST',
  );
  const approve = useApiMutation<undefined, RecruitingOffer>(
    recruitingOfferApprovePath('unset'),
    'POST',
  );
  const send = useApiMutation<undefined, RecruitingOffer>(recruitingOfferSendPath('unset'), 'POST');
  const respond = useApiMutation<RecruitingOfferRespondRequest, RecruitingOffer>(
    recruitingOfferRespondPath('unset'),
    'POST',
  );

  const rows: readonly RecruitingOffer[] = useMemo(() => offers.data?.data ?? [], [offers.data]);

  // candidate_id → name, from the candidates list.
  const candidateName = useMemo(() => {
    const map = new Map<string, string>();
    for (const cand of candidates.data?.data ?? []) map.set(cand.id, cand.full_name);
    return map;
  }, [candidates.data]);

  // The picker options: the applications list, labelled by candidate name where
  // resolvable. `null` when the list is out of reach — the field becomes an id input.
  const applicationOptions: readonly PickerOption[] | null = useMemo(() => {
    if (applications.status !== 'ok' || applications.data === null) return null;
    return applications.data.data.map((app) => ({
      id: app.id,
      label: candidateName.get(app.candidate_id) ?? app.id,
    }));
  }, [applications.status, applications.data, candidateName]);

  const actionPending =
    approve.status === 'pending' || send.status === 'pending' || respond.status === 'pending';
  const actionError = approve.error ?? send.error ?? respond.error;

  const resetActions = (): void => {
    approve.reset();
    send.reset();
    respond.reset();
    setActionOfferId(null);
  };

  const selectApplication = (value: string): void => {
    setApplication(value);
    create.reset();
    resetActions();
  };

  const reloadOffers = (): void => {
    setOffersToken((value) => value + 1);
  };

  const submitCreate = (body: RecruitingOfferCreateRequest): void => {
    if (trimmedApplication === '') return;
    create
      .submit(body, recruitingApplicationOffersPath(trimmedApplication))
      .then(() => {
        reloadOffers();
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const runAction = (offerId: string, action: OfferAction): void => {
    resetActions();
    setActionOfferId(offerId);
    const done = (): void => {
      reloadOffers();
    };
    const swallow = (): void => {
      /* resolves rather than rejects */
    };
    if (action === 'approve') {
      approve.submit(undefined, recruitingOfferApprovePath(offerId)).then(done).catch(swallow);
    } else if (action === 'send') {
      send.submit(undefined, recruitingOfferSendPath(offerId)).then(done).catch(swallow);
    } else {
      const outcome: OfferResponseOutcome = action === 'respond_accepted' ? 'accepted' : 'declined';
      respond.submit({ outcome }, recruitingOfferRespondPath(offerId)).then(done).catch(swallow);
    }
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.offers')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('recruiting.offers.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w5-scope-note">
          {t('recruiting.offers.scopeNote')}
        </p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w5-application">
            {t('recruiting.offers.filter.application')}
          </label>
          {applicationOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w5-application"
                data-testid="w5-application"
                type="text"
                value={application}
                autoComplete="off"
                onChange={(event) => {
                  selectApplication(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('recruiting.offers.filter.applicationIdHint')}</p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w5-application"
              data-testid="w5-application"
              value={application}
              onChange={(event) => {
                selectApplication(event.target.value);
              }}
            >
              <option value="">{t('recruiting.offers.filter.applicationPlaceholder')}</option>
              {applicationOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {trimmedApplication === '' ? (
        <StatusNote
          severity="info"
          title={t('recruiting.offers.pick.title')}
          testId="w5-pick-application"
        >
          <p>{t('recruiting.offers.pick.body')}</p>
        </StatusNote>
      ) : (
        <>
          <CreateOfferForm
            pending={create.status === 'pending'}
            error={create.error}
            created={create.status === 'ok' ? create.data : null}
            onSubmit={submitCreate}
          />

          {offers.status === 'loading' ? (
            <Skeleton
              rows={3}
              variant="block"
              label={t('recruiting.offers.subjectWord')}
              testId="w5-list-skeleton"
            />
          ) : null}
          {offers.status === 'error' && offers.error !== null ? (
            <ApiErrorNote
              error={offers.error}
              onRetry={offers.reload}
              subject={t('recruiting.offers.subjectWord')}
              testId="w5-list-error"
            />
          ) : null}

          {offers.data === null ? null : rows.length === 0 ? (
            <EmptyState
              title={t('recruiting.offers.empty.title')}
              description={t('recruiting.offers.empty.body')}
              testId="w5-list-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="w5-list">
              {rows.map((offer) => (
                <li className="wp-row" key={offer.id} data-testid={`w5-offer-${offer.id}`}>
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity={OFFER_STATUS_BADGE[offer.status]}
                        icon={SEVERITY_ICON[OFFER_STATUS_BADGE[offer.status]]}
                        label={t(OFFER_STATUS_LABEL[offer.status])}
                        testId={`w5-offer-status-${offer.id}`}
                      />{' '}
                      <span data-testid={`w5-offer-salary-${offer.id}`}>
                        {offer.salary_amount === null
                          ? t('recruiting.offers.row.noSalary')
                          : t('recruiting.offers.row.salary', {
                              amount: offer.salary_amount,
                              currency: offer.salary_currency ?? '',
                            })}
                      </span>
                    </p>
                    <p className="wp-small wp-muted">
                      {t('recruiting.offers.row.meta', {
                        start: offer.start_date ?? t('recruiting.offers.row.noStart'),
                        expires:
                          offer.expires_at === null
                            ? t('recruiting.offers.row.noExpiry')
                            : formatInstant(offer.expires_at, locale, timezone),
                      })}
                    </p>
                  </div>
                  <div className="wp-row__figure">
                    <OfferStatusControls
                      offerId={offer.id}
                      status={offer.status}
                      pending={actionOfferId === offer.id && actionPending}
                      error={actionOfferId === offer.id ? actionError : null}
                      onAction={(action) => {
                        runAction(offer.id, action);
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
